const CACHE_KEY = "appmage.appearance";

export class AppearanceManager {
  constructor({ appClient }) {
    this.appClient = appClient;
    this.current = {};
    try { this.apply(JSON.parse(localStorage.getItem(CACHE_KEY) || "{}"), { persist: false, emit: false }); } catch {}
  }

  apply(settings = {}, { persist = true, emit = true } = {}) {
    const previousTheme = this.current.theme;
    const number = (value, fallback, min, max) => {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? Math.max(min, Math.min(max, parsed)) : fallback;
    };
    const font = (value, fallback) => {
      const parsed = String(value || "").trim();
      return parsed && /^[\w\s,'".-]{1,120}$/.test(parsed) ? parsed : fallback;
    };
    const normalized = {
      ...this.current, ...settings,
      theme: ["light", "dark"].includes(settings.theme) ? settings.theme : (this.current.theme || "light"),
      uiFont: font(settings.uiFont ?? this.current.uiFont, "Noto Sans, sans-serif"),
      contentFont: font(settings.contentFont ?? this.current.contentFont, "Noto Sans, sans-serif"),
      terminalFont: font(settings.terminalFont ?? this.current.terminalFont, "JetBrains Mono, Noto Sans Mono, monospace"),
      uiFontSize: number(settings.uiFontSize ?? this.current.uiFontSize, 14, 11, 22),
      contentFontSize: number(settings.contentFontSize ?? this.current.contentFontSize, 14, 10, 32),
      terminalFontSize: number(settings.terminalFontSize ?? this.current.terminalFontSize, 13, 9, 28)
    };
    this.current = normalized;
    const root = document.documentElement;
    root.dataset.bsTheme = normalized.theme;
    root.dataset.contrast = normalized.contrast === "high" ? "high" : "normal";
    root.dataset.reducedMotion = String(Boolean(normalized.reducedMotion));
    root.style.colorScheme = normalized.theme;
    const size = normalized.uiFontSize;
    const tokens = {
      "--app-font-ui": normalized.uiFont, "--app-font-content": normalized.contentFont, "--app-font-terminal": normalized.terminalFont,
      "--app-font-ui-size": `${size}px`, "--app-font-content-size": `${normalized.contentFontSize}px`, "--app-font-terminal-size": `${normalized.terminalFontSize}px`,
      "--font-sans": normalized.uiFont, "--font-mono": normalized.terminalFont, "--bs-body-font-family": normalized.uiFont, "--bs-body-font-size": `${size}px`,
      "--app-accent": normalized.accent || "#4f8cff", "--bs-primary": normalized.accent || "#4f8cff",
      "--ab-text-2xs": `${size * 0.8}px`, "--ab-text-xs": `${size * 0.857}px`, "--ab-text-sm": `${size * 0.929}px`,
      "--ab-text-base": `${size}px`, "--ab-text-md": `${size * 1.143}px`, "--ab-text-lg": `${size * 1.286}px`
    };
    for (const [name, value] of Object.entries(tokens)) root.style.setProperty(name, value);
    if (persist) {
      localStorage.setItem(CACHE_KEY, JSON.stringify(normalized));
      localStorage.setItem("theme", normalized.theme);
    }
    if (emit) {
      if (previousTheme !== normalized.theme) window.dispatchEvent(new CustomEvent("themeChanged", { detail: { theme: normalized.theme } }));
      window.dispatchEvent(new CustomEvent("appearanceChanged", { detail: normalized }));
    }
    return normalized;
  }
}
